import {FC, PropsWithChildren, useState} from 'react';
import {ISvgMenuItem, Svg, SvgMenu} from '@progress-fe/ui-kit';
import {Box, Flex, Center} from '@chakra-ui/react';
import {observer} from 'mobx-react-lite';
import {Link} from 'react-router-dom';
import {useT} from '@progress-fe/core';
import {ProjectOut} from 'api';
import {ROUTES} from 'core/constants';
import {EActionType} from 'core/enums';
import {ProjectRenameModal, ProjectDeleteModal} from 'ui-kit';

interface IProps extends PropsWithChildren {
  project: ProjectOut;
  isDeveloperMode: boolean;
  onToggleDeveloperMode: () => void;
  onRename: (newName: string) => Promise<void>;
  onExport: () => Promise<void>;
  onRestore: () => Promise<void>;
  onDelete: () => Promise<void>;
}

const HeaderFC: FC<IProps> = ({
  project,
  children,
  isDeveloperMode,
  onToggleDeveloperMode,
  onRename,
  onExport,
  onRestore,
  onDelete
}) => {
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const {t} = useT();

  const isInBin = project.deleted;

  const menuItems: ISvgMenuItem<EActionType>[] = [
    {
      icon: 'Dev',
      id: EActionType.DeveloperMode,
      name: t('actions.developerMode'),
      isActive: isDeveloperMode
    },
    {
      icon: 'Pencil',
      id: EActionType.Rename,
      name: t('actions.rename')
    },
    {
      icon: 'Export',
      id: EActionType.Download,
      name: t('actions.download')
    }
  ];
  if (isInBin) menuItems.push({id: EActionType.Restore, name: t('actions.restore'), icon: 'Undo'});
  if (isInBin) menuItems.push({id: EActionType.Delete, name: t('actions.delete'), icon: 'Bin'});
  if (!isInBin) menuItems.push({id: EActionType.ToBin, name: t('actions.toBin'), icon: 'Bin'});

  const handleOnMenuAction = (action: EActionType) => {
    switch (action) {
      case EActionType.DeveloperMode: {
        onToggleDeveloperMode();
        break;
      }
      case EActionType.Rename: {
        setIsRenameOpen(true);
        break;
      }
      case EActionType.Download: {
        onExport().then();
        break;
      }
      case EActionType.Restore: {
        onRestore().then();
        break;
      }
      case EActionType.ToBin: {
        onDelete().then();
        break;
      }
      case EActionType.Delete: {
        setIsDeleteOpen(true);
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <Box boxShadow="shadow_1" position="relative" zIndex={3}>
      {isRenameOpen && (
        <ProjectRenameModal
          initialName={project.name || ''}
          onChange={(v) => onRename?.(v)}
          onClose={() => setIsRenameOpen(false)}
        />
      )}

      {isDeleteOpen && (
        <ProjectDeleteModal
          name={project.name || ''}
          onDelete={() => onDelete()}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}

      <Flex bg="bg" h="48px" p="0 8px 0 0" justify="space-between">
        <Center gap="16px">
          <Link to={ROUTES.MyProjects}>
            <Box _hover={{opacity: '0.8', cursor: 'pointer'}}>
              <Svg name="Favicon" size="s48" />
            </Box>
          </Link>
          <Box fontSize="lg" fontWeight="600" color="black" className="cut-text">
            {project.name}
          </Box>
          <Box>
            <SvgMenu
              items={menuItems}
              menuIcon={'More'}
              menuSize={'normal'}
              variant={'mediumWhite'}
              onSelect={handleOnMenuAction}
            />
          </Box>
        </Center>

        {children}
      </Flex>
    </Box>
  );
};

export const Header = observer(HeaderFC);
